// WebSocket message types for the Moment Live Bridge (FACET-ADR-019).
//
// All messages are JSON with a `type` field. Moment (server) sends these to
// Facet (client) over ws://{host}:{port}.

import type { SimulationTopology, SimulationScenario } from './index.ts';

// ---------------------------------------------------------------------------
// Artifact bundle (shared across initial-load and artifact-update)
// ---------------------------------------------------------------------------

export interface WsArtifacts {
  eventCatalog: unknown;
  impactAnalysis: unknown;
  sagaStateMachines: unknown[];
}

// ---------------------------------------------------------------------------
// WS manifest — lightweight flow index from Moment (no ManifestEntry[])
// ---------------------------------------------------------------------------

export interface WsFlowDescriptor {
  flowId: string;
  flowName: string;
  topology: string;
  scenarioCount: number;
}

export interface WsManifest {
  flows: WsFlowDescriptor[];
  artifacts?: {
    eventCatalog?: string;
    impactAnalysis?: string;
    sagaStateMachines?: string;
  };
}

// ---------------------------------------------------------------------------
// Server → Client messages
// ---------------------------------------------------------------------------

export interface WsInitialLoad {
  type: 'initial-load';
  sessionId: string;
  specFile: string;
  manifest: WsManifest;
  topologies: Record<string, SimulationTopology>;
  scenarios: Record<string, SimulationScenario>;
  artifacts: WsArtifacts;
}

export interface WsTopologyUpdate {
  type: 'topology-update';
  sessionId: string;
  flowId: string;
  topology: SimulationTopology;
  manifest: WsManifest;
}

export interface WsScenarioUpdate {
  type: 'scenario-update';
  sessionId: string;
  flowId: string;
  scenarios: Record<string, SimulationScenario>;
}

export interface WsArtifactUpdate {
  type: 'artifact-update';
  sessionId: string;
  artifacts: WsArtifacts;
}

export interface WsDiagnostic {
  severity: 'error' | 'warning';
  message: string;
}

/** Pipeline phases that can produce errors, plus 'connection' for client-side errors. */
export type WsErrorPhase = 'parse' | 'derive' | 'topology' | 'simulate' | 'connection';

export interface WsError {
  type: 'error';
  sessionId: string;
  phase: WsErrorPhase;
  message: string;
  diagnostics: WsDiagnostic[];
}

export type WsServerMessage =
  | WsInitialLoad
  | WsTopologyUpdate
  | WsScenarioUpdate
  | WsArtifactUpdate
  | WsError;

// ---------------------------------------------------------------------------
// Connection state
// ---------------------------------------------------------------------------

export type WsConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface WsConnectionState {
  status: WsConnectionStatus;
  host: string;
  port: number;
  sessionId: string | null;
  specFile: string | null;
  errorMessage: string | null;
}

export type WsProtocol = 'ws' | 'wss';

export const WS_DEFAULT_HOST = 'localhost';
export const WS_DEFAULT_PORT = 4321;
export const WS_DEFAULT_PROTOCOL: WsProtocol = 'ws';

// ---------------------------------------------------------------------------
// Close codes from Moment
// ---------------------------------------------------------------------------

/** Clean shutdown — do NOT auto-reconnect. */
export const WS_CLOSE_NORMAL = 1000;
/** Going away (server restart) — auto-reconnect. */
export const WS_CLOSE_GOING_AWAY = 1001;
/** Maximum clients reached — show error, don't retry. */
export const WS_CLOSE_MAX_CLIENTS = 1013;
